import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Building2, Trash2, RotateCcw, Globe, MapPin, Users as UsersIcon, ExternalLink, Upload, FileText, Plus, ChevronDown, ChevronUp, UserPlus, X } from 'lucide-react';
import { toast } from 'sonner';

const API = '';

export function AdminPartnersTab() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [partnerDocs, setPartnerDocs] = useState({});
  const [partnerTeams, setPartnerTeams] = useState({});  // sponsor_id -> [members]
  const [allMembers, setAllMembers] = useState([]);
  const [memberSearchByPartner, setMemberSearchByPartner] = useState({});
  const [uploadDialog, setUploadDialog] = useState(null); // sponsor_id or null
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', file: null });

  useEffect(() => { loadPartners(); loadAllMembers(); }, []);

  const loadAllMembers = async () => {
    try {
      const res = await fetch(`${API}/api/members`, { credentials: 'include' });
      if (res.ok) setAllMembers(await res.json());
    } catch {}
  };

  const loadPartners = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/partners?limit=100`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPartners(data.partners || []);
      }
    } catch {}
    setLoading(false);
  };

  const loadDocs = async (sponsorId) => {
    try {
      const res = await fetch(`${API}/api/partners/${sponsorId}/documents`, { credentials: 'include' });
      if (res.ok) {
        const docs = await res.json();
        setPartnerDocs(prev => ({ ...prev, [sponsorId]: docs }));
      }
    } catch {}
  };

  const loadTeam = async (sponsorId) => {
    try {
      const res = await fetch(`${API}/api/partners/${sponsorId}/team`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setPartnerTeams(prev => ({ ...prev, [sponsorId]: d.members || [] }));
      }
    } catch {}
  };

  const linkMember = async (partner, member) => {
    try {
      const current = partner.manual_team_user_ids || [];
      if (current.includes(member.user_id)) { toast.info(`${member.name} is already linked`); return; }
      const next = [...current, member.user_id];
      const res = await fetch(`${API}/api/sponsors/${partner.sponsor_id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: partner.name, description: partner.description || '',
          logo_url: partner.logo_url || '', logo_description: partner.logo_description || '',
          website: partner.website || '', contact_email: partner.contact_email || '',
          categories: partner.categories || [], partner_type: partner.partner_type || 'Partner',
          manual_team_user_ids: next,
        }),
      });
      if (!res.ok) throw new Error('Failed');
      // Optimistic local update + refresh team
      setPartners(prev => prev.map(p => p.sponsor_id === partner.sponsor_id ? { ...p, manual_team_user_ids: next } : p));
      setMemberSearchByPartner(prev => ({ ...prev, [partner.sponsor_id]: '' }));
      loadTeam(partner.sponsor_id);
      toast.success(`Linked ${member.name}`);
    } catch { toast.error('Failed to link member'); }
  };

  const unlinkMember = async (partner, member) => {
    if (!(await window.appConfirm(`Unlink ${member.name} from ${partner.name}?`))) return;
    try {
      const current = partner.manual_team_user_ids || [];
      const next = current.filter(uid => uid !== member.user_id);
      const res = await fetch(`${API}/api/sponsors/${partner.sponsor_id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: partner.name, description: partner.description || '',
          logo_url: partner.logo_url || '', logo_description: partner.logo_description || '',
          website: partner.website || '', contact_email: partner.contact_email || '',
          categories: partner.categories || [], partner_type: partner.partner_type || 'Partner',
          manual_team_user_ids: next,
        }),
      });
      if (!res.ok) throw new Error('Failed');
      setPartners(prev => prev.map(p => p.sponsor_id === partner.sponsor_id ? { ...p, manual_team_user_ids: next } : p));
      loadTeam(partner.sponsor_id);
      toast.success(`Unlinked ${member.name}`);
    } catch { toast.error('Failed to unlink'); }
  };

  const toggleExpand = (sponsorId) => {
    if (expandedId === sponsorId) {
      setExpandedId(null);
    } else {
      setExpandedId(sponsorId);
      if (!partnerDocs[sponsorId]) loadDocs(sponsorId);
      if (!partnerTeams[sponsorId]) loadTeam(sponsorId);
    }
  };

  const handleDelete = async (partner) => {
    const isCompanyPartner = partner.sponsor_id?.startsWith('partner_from_');
    const msg = isCompanyPartner
      ? `Remove "${partner.name}" from the Partners page? This will also uncheck "Mark as Partner" on the company.`
      : `Delete "${partner.name}" from the Partners page? This cannot be undone.`;
    if (!(await window.appConfirm(msg))) return;

    try {
      if (isCompanyPartner) {
        const companyId = partner.source_company_id || partner.sponsor_id.replace('partner_from_', '');
        const res = await fetch(`${API}/api/companies/${companyId}`, {
          method: 'PUT', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ is_partner: false }),
        });
        if (!res.ok) throw new Error('Failed to update company');
      } else {
        const res = await fetch(`${API}/api/sponsors/${partner.sponsor_id}`, {
          method: 'DELETE', credentials: 'include',
        });
        if (!res.ok) throw new Error('Failed to delete partner');
      }
      toast.success(`"${partner.name}" removed from Partners`);
      loadPartners();
    } catch (e) { toast.error(e.message); }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.file) { toast.error('Select a file to upload'); return; }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('description', form.description);
      fd.append('file', form.file);
      const res = await fetch(`${API}/api/partners/${uploadDialog}/documents`, { method: 'POST', body: fd, credentials: 'include' });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Upload failed'); }
      toast.success('Document uploaded');
      setUploadDialog(null);
      setForm({ title: '', description: '', file: null });
      loadDocs(expandedId);
    } catch (err) { toast.error(err.message); }
    setUploading(false);
  };

  const handleDeleteDoc = async (docId, sponsorId) => {
    if (!(await window.appConfirm('Delete this document?'))) return;
    try {
      const res = await fetch(`${API}/api/partners/documents/${docId}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error();
      toast.success('Document deleted');
      loadDocs(sponsorId);
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-4" data-testid="admin-partners-content">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="text-xl font-bold font-heading">Partner Directory Management</h2>
          <p className="text-sm text-slate-500 mt-0.5">Manage partners and upload documents for each partner.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={async () => {
            if (!(await window.appConfirm('Find and merge duplicate partner/sponsor records that share a company? Banners and documents are preserved.'))) return;
            try {
              const res = await fetch(`${API}/api/admin/sponsors/dedupe`, { method: 'POST', credentials: 'include' });
              const d = await res.json();
              if (!res.ok) throw new Error(d.detail || 'Failed');
              toast.success(`Merged ${d.merged_groups} duplicate group(s); deleted ${d.deleted_records} record(s).`);
              loadPartners();
            } catch (e) { toast.error(e.message || 'Failed'); }
          }} data-testid="dedupe-partners-btn" className="text-amber-700 hover:text-amber-800">
            Dedupe duplicates
          </Button>
          <Button size="sm" variant="outline" onClick={loadPartners} data-testid="refresh-partners-btn">
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Upload Dialog */}
      <Dialog open={!!uploadDialog} onOpenChange={() => setUploadDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Upload Partner Document</DialogTitle></DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Document Title *</label>
              <Input data-testid="partner-doc-title" value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Company Brochure, Case Study" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Description</label>
              <Textarea data-testid="partner-doc-description" value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Brief description of the document" rows={2} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">File *</label>
              <Input data-testid="partner-doc-file" type="file"
                onChange={e => setForm(f => ({ ...f, file: e.target.files?.[0] || null }))}
                className="mt-1" />
            </div>
            <Button type="submit" disabled={uploading} data-testid="submit-partner-doc-btn"
              className="w-full bg-brand-navy hover:bg-brand-dark text-white">
              {uploading ? 'Uploading...' : 'Upload Document'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full" />
        </div>
      ) : partners.length === 0 ? (
        <Card className="p-8 text-center">
          <Building2 className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-500 text-sm">No partners in the directory.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          <p className="text-sm text-slate-500">{partners.length} partner{partners.length !== 1 ? 's' : ''} listed</p>
          {partners.map(partner => {
            const isCompanyPartner = partner.sponsor_id?.startsWith('partner_from_');
            const isExpanded = expandedId === partner.sponsor_id;
            const docs = partnerDocs[partner.sponsor_id] || [];
            const team = partnerTeams[partner.sponsor_id] || [];
            return (
              <Card key={partner.sponsor_id} className="overflow-hidden" data-testid={`admin-partner-${partner.sponsor_id}`}>
                <div className="p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                      onClick={() => toggleExpand(partner.sponsor_id)}>
                      <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {partner.logo_url ? (
                          <img src={partner.logo_url} alt="" className="w-full h-full object-contain rounded-lg p-1" />
                        ) : (
                          <Building2 className="h-5 w-5 text-brand-navy/40" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-sm text-slate-800 truncate">{partner.name}</p>
                          {/* Always show "Partner" pill — every record on this page is a Partner */}
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-slate-600 text-white">Partner</span>
                          {/* Tier-specific pill (Sponsor / Exhibitor) — render in addition */}
                          {partner.partner_type === 'Sponsor' && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-amber-500 text-white">Sponsor</span>
                          )}
                          {partner.partner_type === 'Premium Sponsor' && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-amber-600 text-white">Premium Sponsor</span>
                          )}
                          {partner.partner_type === 'Exhibitor' && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-brand-sage text-white">Exhibitor</span>
                          )}
                          {isCompanyPartner && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold">FROM COMPANY</span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-3 text-xs text-slate-400 mt-0.5">
                          {partner.sector && <span><Building2 className="h-3 w-3 inline mr-0.5" />{partner.sector}</span>}
                          {(partner.hq_location || partner.hq_country) && (
                            <span><MapPin className="h-3 w-3 inline mr-0.5" />{[partner.hq_location, partner.hq_country].filter(Boolean).join(', ')}</span>
                          )}
                          {partner.company_size && <span><UsersIcon className="h-3 w-3 inline mr-0.5" />{partner.company_size}</span>}
                          {partner.website && (
                            <a href={partner.website} target="_blank" rel="noopener noreferrer"
                              className="text-brand-navy hover:underline flex items-center gap-0.5" onClick={e => e.stopPropagation()}>
                              <Globe className="h-3 w-3" />{partner.website.replace(/^https?:\/\//, '').split('/')[0]}
                              <ExternalLink className="h-2.5 w-2.5" />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button size="sm" variant="ghost" onClick={() => toggleExpand(partner.sponsor_id)}
                        className="text-slate-400 hover:text-slate-600">
                        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDelete(partner)} data-testid={`delete-partner-${partner.sponsor_id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Expanded: Team + Documents Section */}
                {isExpanded && (
                  <div className="border-t bg-slate-50 px-4 py-3 space-y-5">
                    {/* Linked Team Members */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                          <UsersIcon className="h-4 w-4 text-brand-navy" />
                          Linked Team Members
                          {team.length > 0 && <span className="text-slate-400 font-normal">({team.length})</span>}
                        </h4>
                      </div>
                      {team.length === 0 ? (
                        <p className="text-xs text-slate-400 py-1 italic">No members linked yet. Search below to add.</p>
                      ) : (
                        <div className="flex flex-wrap gap-2 mb-3" data-testid={`partner-team-${partner.sponsor_id}`}>
                          {team.map(m => (
                            <span key={m.user_id} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-full pl-1 pr-2 py-0.5">
                              {m.picture ? (
                                <img src={m.picture} alt="" className="w-5 h-5 rounded-full object-cover" />
                              ) : (
                                <div className="w-5 h-5 rounded-full bg-brand-navy/10 flex items-center justify-center text-[9px] font-bold text-brand-navy">{m.name?.charAt(0)}</div>
                              )}
                              <span className="text-xs font-medium text-slate-700">{m.name}</span>
                              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${m.link_reason === 'manual' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                                {m.link_reason === 'manual' ? 'manual' : 'auto'}
                              </span>
                              {m.link_reason === 'manual' && (
                                <button type="button" onClick={() => unlinkMember(partner, m)}
                                  className="hover:text-red-500 text-slate-400" data-testid={`unlink-member-${partner.sponsor_id}-${m.user_id}`}>
                                  <X className="h-3 w-3" />
                                </button>
                              )}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Add member search */}
                      <div className="relative max-w-md">
                        <UserPlus className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                          placeholder="Search a platform member to link…"
                          value={memberSearchByPartner[partner.sponsor_id] || ''}
                          onChange={e => setMemberSearchByPartner(prev => ({ ...prev, [partner.sponsor_id]: e.target.value }))}
                          className="pl-8 text-sm"
                          data-testid={`link-member-search-${partner.sponsor_id}`}
                        />
                        {(memberSearchByPartner[partner.sponsor_id] || '').length >= 2 && (
                          <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {(() => {
                              const q = memberSearchByPartner[partner.sponsor_id].toLowerCase();
                              const linkedIds = new Set((team || []).map(m => m.user_id));
                              const matches = allMembers
                                .filter(m => !linkedIds.has(m.user_id) && (m.name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q)))
                                .slice(0, 8);
                              if (matches.length === 0) return <p className="px-3 py-2 text-xs text-slate-400">No members found</p>;
                              return matches.map(m => (
                                <button key={m.user_id} type="button"
                                  onClick={() => linkMember(partner, m)}
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"
                                  data-testid={`link-member-${partner.sponsor_id}-${m.user_id}`}>
                                  {m.picture ? (
                                    <img src={m.picture} alt="" className="w-6 h-6 rounded-full object-cover" />
                                  ) : (
                                    <div className="w-6 h-6 rounded-full bg-brand-navy/10 flex items-center justify-center text-[10px] font-bold text-brand-navy">{m.name?.charAt(0)}</div>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-slate-700 truncate">{m.name}</p>
                                    {m.profile?.organisation && <p className="text-[10px] text-slate-400 truncate">{m.profile.organisation}</p>}
                                  </div>
                                </button>
                              ));
                            })()}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Documents */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-slate-700">
                          Documents {docs.length > 0 && <span className="text-slate-400 font-normal">({docs.length})</span>}
                        </h4>
                        <Button size="sm" variant="outline" onClick={() => { setUploadDialog(partner.sponsor_id); setForm({ title: '', description: '', file: null }); }}
                          data-testid={`upload-doc-${partner.sponsor_id}`}>
                          <Plus className="h-3.5 w-3.5 mr-1" /> Upload Document
                        </Button>
                      </div>
                      {docs.length === 0 ? (
                        <p className="text-xs text-slate-400 py-2">No documents uploaded for this partner.</p>
                      ) : (
                        <div className="space-y-2">
                          {docs.map(doc => (
                            <div key={doc.doc_id} className="flex items-center justify-between gap-3 p-2.5 bg-white rounded-md border border-slate-200"
                              data-testid={`partner-doc-${doc.doc_id}`}>
                              <div className="flex items-center gap-2.5 min-w-0">
                                <FileText className="h-4 w-4 text-brand-navy flex-shrink-0" />
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-slate-700 truncate">{doc.title}</p>
                                  <p className="text-xs text-slate-400">{doc.filename} ({(doc.size / 1024 / 1024).toFixed(1)} MB)</p>
                                </div>
                              </div>
                              <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                                onClick={() => handleDeleteDoc(doc.doc_id, partner.sponsor_id)}
                                data-testid={`delete-doc-${doc.doc_id}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
